declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: any;
    __loadIntercom?: () => void;
    __agoraIntercomLauncherHidden?: boolean;
  }
}

export type IntercomWebToken = { token: string; expires_at: number };

let activeIdentity: string | null = null;
let authPending = false;

export const isIntercomAuthPending = () => authPending;

export const setIntercomLauncherHidden = (hidden: boolean) => {
  window.__agoraIntercomLauncherHidden = hidden;
  if (window.intercomSettings && typeof window.intercomSettings === 'object') {
    window.intercomSettings.hide_default_launcher = hidden || authPending;
  }
  if (!authPending) {
    try {
      window.Intercom?.('update', { hide_default_launcher: hidden });
    } catch {
      // Support UI must never interrupt navigation.
    }
  }
};

// Never send a bare user_id/email to the web Messenger: it would allow account
// impersonation while JWT enforcement is being rolled out in Intercom.
export const pauseIntercomIdentity = () => {
  authPending = true;
  activeIdentity = null;
  if (window.intercomSettings) {
    delete window.intercomSettings.intercom_user_jwt;
    window.intercomSettings.hide_default_launcher = true;
  }
  try {
    window.Intercom?.('shutdown');
  } catch {
    // The SDK may not have loaded yet; its stub will queue the shutdown.
  }
};

export const bootAuthenticatedIntercom = (identity: string, token: string) => {
  if (!identity || !token) return;
  const isRefresh = activeIdentity === identity;
  authPending = false;
  const settings = {
    ...window.intercomSettings,
    app_id: 'lt3sf6pd',
    intercom_user_jwt: token,
    hide_default_launcher: window.__agoraIntercomLauncherHidden ?? false
  };
  window.intercomSettings = settings;
  if (isRefresh) {
    window.Intercom?.('update', { intercom_user_jwt: token });
  } else {
    window.Intercom?.('shutdown');
    window.Intercom?.('boot', settings);
  }
  activeIdentity = identity;
  window.__loadIntercom?.();
};

export const clearIntercomIdentity = () => {
  authPending = false;
  activeIdentity = null;
  if (window.intercomSettings) delete window.intercomSettings.intercom_user_jwt;
  try {
    window.Intercom?.('shutdown');
  } catch {
    // Logout must still complete if support is unavailable.
  }
};

export const updateIntercomPhone = (_phone: string) => {
  // The server owns the verified phone claim. Refresh the JWT after the API
  // persists the change instead of accepting an unsigned browser update.
  window.dispatchEvent(new Event('agora:intercom-identity-refresh'));
};

export const sendIntercomEvent = (event: string) => {
  if (!window.Intercom || !activeIdentity || authPending) return;

  try {
    if (window.intercomSettings?.intercom_user_jwt) {
      window.Intercom('boot', window.intercomSettings);
    }
    window.Intercom('trackEvent', event);
  } catch {
    // Support analytics must never break the product flow.
  }
};
